import { useEffect, useState } from 'react'

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { GET_ALL_DESKTOP_SETTINGS_QUERY_KEY } from 'shared/api/containerApi/requests/getAllDesktopSettings'
import { useWallpaperStore } from 'shared/stores/wallpaperStore'
import { isTesting } from 'shared/utils/isTesting'

import {
  WallpaperRevisionChangedError,
  loadAnimatedWallpaper,
  useAnimatedWallpaperCatalog
} from './catalog'
import { resolveAnimatedWallpaperScene } from './resolveAnimatedWallpaperScene'

export const useAnimatedWallpaperScene = (id: string) => {
  const queryClient = useQueryClient()
  const entry = useAnimatedWallpaperCatalog(state =>
    state.wallpapers.find(wallpaper => wallpaper.id === id)
  )
  // Revisions are immutable, so a loaded definition never goes stale.
  const { data: definition } = useQuery({
    queryKey: ['predefinedWallpaperAnimation', entry?.id, entry?.revision],
    queryFn: () => loadAnimatedWallpaper(entry!),
    enabled: !!entry,
    staleTime: Infinity,
    retry: (failureCount, error) =>
      !(error instanceof WallpaperRevisionChangedError) && failureCount < 3,
    onError: error => {
      if (error instanceof WallpaperRevisionChangedError)
        void queryClient.invalidateQueries([GET_ALL_DESKTOP_SETTINGS_QUERY_KEY])
    }
  })
  const seasonPreview = useWallpaperStore(state =>
    isTesting() && state.currentWallpaper.id === id ? state.seasonPreview : null
  )
  const [date, setDate] = useState(() => new Date())
  useEffect(() => {
    if (definition?.renderer !== 'seasonal') return
    let timer: ReturnType<typeof setTimeout>
    const update = () => {
      clearTimeout(timer)
      const now = new Date()
      setDate(now)
      const midnight = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() + 1
      )
      timer = setTimeout(update, midnight.getTime() - now.getTime() + 100)
    }
    const resume = () => {
      if (!document.hidden) update()
    }
    update()
    document.addEventListener('visibilitychange', resume)
    return () => {
      clearTimeout(timer)
      document.removeEventListener('visibilitychange', resume)
    }
  }, [definition])
  return definition
    ? resolveAnimatedWallpaperScene(definition, date, seasonPreview)
    : undefined
}
